import { SetupProvider, parseSetupRedirectParams, useIsOnboardingComplete, useSetupContext } from "@sg-protect/shell";
import { Navigate, Outlet, useLocation, useSearchParams } from "react-router";
import { useSetupExit } from "../../hooks/useSetupExit.ts";
import { useSetupTenantHeaderMiddleware } from "../../hooks/useSetupTenantHeaderMiddleware.ts";
import { usePaths } from "../../paths.ts";
import { STEPS_THAT_STAY_ON_SETUP } from "../../utils/setupExit.ts";
import {
    TenantAssessmentAuthenticatedLayout,
    type TenantAssessmentAuthenticatedLayoutProps
} from "./TenantAssessmentAuthenticatedLayout.tsx";

type SetupRouteLayoutProps = Omit<
    TenantAssessmentAuthenticatedLayoutProps,
    "isFullScreen" | "isDistractionFree" | "isBannersHidden" | "onClose" | "mainContent"
>;

export function SetupCompletionRedirect() {
    const { home } = usePaths();
    const isOnboardingComplete = useIsOnboardingComplete();
    const [searchParams] = useSearchParams();
    const { state } = useLocation();
    const { hasHandoff } = useSetupContext();

    const hasError = state?.consentError === true || searchParams.has("error");
    const step = searchParams.get("step");
    const staysOnSetup = step !== null && STEPS_THAT_STAY_ON_SETUP.has(step);
    const { origin, returnTo } = parseSetupRedirectParams(searchParams);
    const hasPendingConsentHandoff = origin !== null || Boolean(returnTo) || hasHandoff;

    if (isOnboardingComplete && !hasError && !staysOnSetup && !hasPendingConsentHandoff) {
        return <Navigate to={home} replace />;
    }

    return <Outlet />;
}

function SetupRouteContent() {
    const isMiddlewareInstalled = useSetupTenantHeaderMiddleware();

    if (!isMiddlewareInstalled) {
        return null;
    }

    return (
        <SetupProvider>
            <SetupCompletionRedirect />
        </SetupProvider>
    );
}

export function SetupRouteLayout(props: SetupRouteLayoutProps) {
    const { closeSetup } = useSetupExit();

    return (
        <TenantAssessmentAuthenticatedLayout
            {...props}
            isFullScreen
            isDistractionFree
            isBannersHidden
            onClose={closeSetup}
            mainContent={<SetupRouteContent />}
        />
    );
}
